import { useForm, useField } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function AdminNewBooking() {
  const navigate = useNavigate();

  const createBookingMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      email: string;
      startDate: string;
      endDate: string;
      bookingReference: string;
    }) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("bigBearJWTToken")}`,
        },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to create booking");
      }

      return res.json();
    },
    onSuccess: () => {
      navigate("/admin/bookings");
    },
  });

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      startDate: "",
      endDate: "",
      bookingReference: "",
    },
    onSubmit: ({ value }) => {
      createBookingMutation.mutate(value);
    },
  });

  const nameField = useField({
    form,
    name: "name",
    validators: {
      onChange: ({ value }) => (!value.trim() ? "Name is required" : undefined),
    },
  });

  const emailField = useField({
    form,
    name: "email",
    validators: {
      onChange: ({ value }) => {
        if (!value.trim()) return "Email is required";
        if (!isValidEmail(value)) return "Invalid email address";
      },
    },
  });

  const startField = useField({
    form,
    name: "startDate",
    validators: {
      onChange: ({ value }) =>
        !value.trim() ? "Start date is required" : undefined,
    },
  });

  const endField = useField({
    form,
    name: "endDate",
    validators: {
      onChange: ({ value }) =>
        !value.trim() ? "End date is required" : undefined,
    },
  });

  const bookingRefField = useField({
    form,
    name: "bookingReference",
  });

  const renderError = (meta: any) =>
    meta.isTouched && meta.errors.length > 0 ? (
      <p className="mt-1 text-sm text-red-600">{meta.errors[0]}</p>
    ) : null;

  return (
    <div className="flex justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-md">
        <h2 className="mb-6 text-center text-2xl font-semibold text-[#006838]">
          New Booking
        </h2>

        {createBookingMutation.isError && (
          <div className="mb-4 text-sm text-red-600">
            {(createBookingMutation.error as Error).message}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Booking Reference
            </label>
            <input
              type="text"
              value={bookingRefField.state.value}
              onChange={(e) => bookingRefField.handleChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[#006838] focus:ring-[#006838] focus:ring-1 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nameField.state.value}
              onChange={(e) => nameField.handleChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[#006838] focus:ring-[#006838] focus:ring-1 focus:outline-none"
            />
            {renderError(nameField.state.meta)}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={emailField.state.value}
              onChange={(e) => emailField.handleChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[#006838] focus:ring-[#006838] focus:ring-1 focus:outline-none"
            />
            {renderError(emailField.state.meta)}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Check in <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startField.state.value}
              onChange={(e) => startField.handleChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[#006838] focus:ring-[#006838] focus:ring-1 focus:outline-none"
            />
            {renderError(startField.state.meta)}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Check out <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={endField.state.value}
              onChange={(e) => endField.handleChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[#006838] focus:ring-[#006838] focus:ring-1 focus:outline-none"
            />
            {renderError(endField.state.meta)}
          </div>

          <button
            type="submit"
            disabled={createBookingMutation.isPending}
            className="mt-4 w-full rounded-md bg-[#006838] px-4 py-2 text-white hover:bg-green-800 transition disabled:opacity-50"
          >
            {createBookingMutation.isPending ? "Saving..." : "Create Booking"}
          </button>
        </form>
      </div>
    </div>
  );
}
